import React, { useContext } from 'react';
import Head from 'next/head';
import { useTranslatableField } from '@/utils/useTranslatableField';
import { PageContext } from '@/context/PageContext';
import { useFeatureFlags } from '@/context/FeatureFlagsContext';
import { COM_CONTENT_NO_INDEX_FLAG, ROBOTS_NOINDEX_VALUE } from '@/constants/robots';
import { useConsoleLogVersions } from 'utils/useConsoleLogVersions';
import { determineThemeName } from 'utils/determineUrls';
import { useGetContentTranslations } from 'utils/useGetContentTranslations';
import { shouldNoIndex } from 'utils/shouldNoIndex';
import { shouldTrackUmami } from 'utils/shouldTrackUmami';
import { SurveyStructuredData } from './SurveyStructuredData';
import { SurveyOpenGraph } from './SurveyOpenGraphTags';
import { GoogleAnalytics } from './GoogleAnalytics';
import { FacebookPixel } from './FacebookPixel';
import { HrefLang } from './HrefLang';
import { UmamiAnalytics } from './UmamiAnalytics';
import { ArticleStructuredData } from './PageStructuredData/ArticleStructuredData';
import { FaqStructuredData } from './PageStructuredData/FaqStructuredData';
import { GovermentOrganizationStructuredData } from './PageStructuredData/GovermentOrganizationStructuredData';
import { PageOpenGraph } from './PageOpenGraphTags';
import { determinePageDescription } from './PageHelpers';

const EngageGoogleAnalyticsId = 'G-0JKWS6MQGB';

export function Metadata() {
  const { theme, survey, page, urlParams, canonicalHostname } = useContext(PageContext);
  const featureFlags = useFeatureFlags();
  const { translateField } = useTranslatableField(urlParams?.locale);
  const { translate } = useGetContentTranslations();
  let title = determineThemeName(theme, translateField) || '';
  if (survey) {
    title = translateField(survey.title_for_display);
  }
  if (page) {
    title = translate(page.translations, 'title');
  }
  if (!title && urlParams?.preview) {
    title = `${survey?.client?.name} Preview`;
  }
  useConsoleLogVersions();
  const gaIds = [EngageGoogleAnalyticsId];
  if (theme?.googleTrackingId) {
    gaIds.push(theme?.googleTrackingId);
  }
  return (
    <>
      <Head>
        <title>{title}</title>
        {page && <meta name="description" content={determinePageDescription(page, translate)} />}
        {shouldNoIndex({
          appEnv: globalThis?.process?.env.NEXT_PUBLIC_APP_ENV,
          isPreview: urlParams?.preview,
          comContentNoIndex: featureFlags?.[COM_CONTENT_NO_INDEX_FLAG],
        }) && <meta name="robots" content={ROBOTS_NOINDEX_VALUE} />}
        <link rel="icon" href="https://logo.zencity.io/favicon.ico" sizes="32x32" />
        <link rel="icon" href="https://logo.zencity.io/brandmark.svg" type="image/svg+xml" />
      </Head>
      <GoogleAnalytics gaIds={gaIds} clientId={theme?.clientId} />
      <FacebookPixel facebookId={theme?.facebookTrackingId} />
      <HrefLang />
      {shouldTrackUmami({ page, umamiWebsiteId: theme?.umamiWebsiteId, urlParams }) && (
        <UmamiAnalytics
          umamiWebsiteId={theme!.umamiWebsiteId!}
          pageId={page!.id}
          canonicalHostname={canonicalHostname}
        />
      )}
      {survey && (
        <>
          <SurveyStructuredData survey={survey} />
          <SurveyOpenGraph survey={survey} />
        </>
      )}
      {page && (
        <>
          <ArticleStructuredData page={page} />
          <FaqStructuredData page={page} />
          <GovermentOrganizationStructuredData page={page} />
          <PageOpenGraph page={page} />
        </>
      )}
    </>
  );
}
